import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeStake, IPrizeElement } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData } from '@/components/game/_domain/server/prize/prize-data';
import { getGameConfiguration } from '@/components/game/_domain/helpers';
import { ConfigurationPaths } from '@/app-modules/config/models';
import { BubbleBingoPrizeType, BubbleBingoPromoPrizeType, getPrizeTypeName, getPromoPrizeTypeName } from './prize-type';
import { safeCast } from '@/helper';

class BubbleBingoPrizeData extends PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  private get stakeValues(): number[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = this.getGameConfigurationPaths();
    const stakeValues = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.stakevalues);
    return stakeValues.split(',').map((item) => parseInt(item, 10));
  }

  private get prizeTypes(): BubbleBingoPrizeType[] {
    return [
      BubbleBingoPrizeType.Jackpot,
      BubbleBingoPrizeType.Prize
    ];
  }

  private get promoPrizeTypes(): BubbleBingoPromoPrizeType[] {
    return [
      BubbleBingoPromoPrizeType.Jackpot,
      BubbleBingoPromoPrizeType.PrizeGreen,
      BubbleBingoPromoPrizeType.PrizeRed,
      BubbleBingoPromoPrizeType.PrizeBlue
    ];
  }

  private getGameConfigurationPaths() {
    return ConfigurationPaths.forGame('bubblebingo');
  }

  private get jackpotCalls(): number[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = this.getGameConfigurationPaths();
    const jackpotCalls = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.jackpotcalls);
    return jackpotCalls.split(',').map((item) => parseInt(item, 10));
  }

  private get jackpotPrizes(): number[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = this.getGameConfigurationPaths();
    const jackpotPrizes = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.jackpotprize);
    return jackpotPrizes.split(',').map((item) => parseInt(item, 10));
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: this.stakeValues.map((stakeValue) => this.getPrizeStakeForStakeValue(stakeValue, node))
    };
  }

  private getPrizeStakeForStakeValue(stakeValue: number, node: IXmlNode): IPrizeStake {
    return {
      stakeValue,
      ticketBooks: [
        {
          id: 'default',
          name: 'Default',
          prizes: this.getPrizes(stakeValue, node)
        }
      ]
    };
  }

  private getPrizes(stakeValue: number, node: IXmlNode): IPrizeElement[] {
    const isAltPrizesGame = safeCast(node.attributes.altprizes, false);
    if (isAltPrizesGame) {
      return this.promoPrizeTypes.map((prizeType) => {
        const isJackpot = prizeType === BubbleBingoPromoPrizeType.Jackpot;

        return {
          name: getPromoPrizeTypeName(prizeType),
          prize: this.getPrizeValueForPromoPrizeTypeAndStakeValue(prizeType, stakeValue, node),
          disablePrize: false,
          isJackpot,
          jackpotCalls: isJackpot ? this.getJackpotCallsForPrizeType(prizeType) : 0
        };
      });
    }

    return this.prizeTypes.map((prizeType) => {
      const isJackpot = prizeType === BubbleBingoPrizeType.Jackpot;

      return {
        name: getPrizeTypeName(prizeType),
        prize: this.getPrizeValueForPrizeTypeAndStakeValue(prizeType, stakeValue, node),
        disablePrize: false,
        isJackpot,
        jackpotCalls: isJackpot ? this.getJackpotCallsForPrizeType(prizeType) : 0
      };
    });
  }

  private getJackpotCallsForPrizeType(prizeType: BubbleBingoPrizeType | BubbleBingoPromoPrizeType): number {
    if (prizeType === BubbleBingoPrizeType.Jackpot || prizeType === BubbleBingoPromoPrizeType.Jackpot) {
      return this.jackpotCalls[0];
    }
    return 0;
  }

  private getPrizeValueForPrizeTypeAndStakeValue(prizeType: BubbleBingoPrizeType, stakeValue: number, node: IXmlNode): number {
    if (prizeType === BubbleBingoPrizeType.Jackpot) {
      return stakeValue * this.jackpotPrizes[0];
    }

    const prizeStakeType = `prize${stakeValue}`;
    return Number(node.attributes[prizeStakeType]);
  }

  private getPrizeValueForPromoPrizeTypeAndStakeValue(prizeType: BubbleBingoPromoPrizeType, stakeValue: number, node: IXmlNode): number {
    if (prizeType === BubbleBingoPromoPrizeType.Jackpot) {
      return stakeValue * this.jackpotPrizes[0];
    }

    const prizeAttributeName = `prize${stakeValue}`;
    const prizes = this.getPrizeAmounts(prizeAttributeName, node);
    if (prizeType === BubbleBingoPromoPrizeType.PrizeGreen) {
      return prizes[0];
    }
    if (prizeType === BubbleBingoPromoPrizeType.PrizeRed) {
      return prizes[1];
    }
    if (prizeType === BubbleBingoPromoPrizeType.PrizeBlue) {
      return prizes[2];
    }
  }

  private getPrizeAmounts(prizeAttributeName: string, node: IXmlNode): number[] {
    return node.attributes[prizeAttributeName].split(',').map((item) => {
      return parseInt(item, 10);
    });
  }
}

class BubbleBingoPrizeDataForNextGame extends BubbleBingoPrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class BubbleBingoCatchUpData extends CatchUpData {
  private prizeData: BubbleBingoPrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BubbleBingoPrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class BubbleBingoResetData extends ResetData {
  private prizeData: BubbleBingoPrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BubbleBingoPrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class BubbleBingoCallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class BubbleBingoNextGameData extends NextGameData {
  private prizeData: BubbleBingoPrizeDataForNextGame;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new BubbleBingoPrizeDataForNextGame(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  BubbleBingoCatchUpData,
  BubbleBingoResetData,
  BubbleBingoCallsData,
  BubbleBingoNextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/bubble-bingo/_domain/models/prize.ts